import { ConvertIcon } from '@approvalmax/ui';
import { Box, Button, Flex, Grid, Text } from '@approvalmax/ui/src/components';
import { domHelpers } from '@approvalmax/utils';
import { useSetAtom } from 'jotai';
import { ComponentType, useCallback } from 'react';
import { useHover } from 'react-use';

import { convertCurrencyPopupState } from '../ConvertCurrencyPopup/ConvertCurrencyPopup.states';
import { Loading } from './WalletCard.loading';
import { messages } from './WalletCard.messages';
import { ChildrenComponents, WalletCardProps } from './WalletCard.types';

const qa = 'wallet-card';

export const WalletCard = ((props: WalletCardProps) => {
    const { currencyCode, currencyName, IconFlag, controls, balance, disabled = false, canConvert } = props;

    const setConvertCurrencyPopupState = useSetAtom(convertCurrencyPopupState);

    const handleConvertClick = useCallback(() => {
        setConvertCurrencyPopupState({
            isOpen: true,
            fromDefaultCurrency: typeof currencyCode === 'string' ? currencyCode : undefined,
            toDefaultCurrency: undefined,
        });
    }, [currencyCode, setConvertCurrencyPopupState]);

    const [hoverable] = useHover((hover) => (
        <Box
            color={hover && !disabled ? 'blue10' : 'white100'}
            bordered
            borderColor={hover && !disabled ? 'blue80' : 'white100'}
            radius='medium'
            spacing='24'
            // to behave as masonry-like within grid
            height='max-content'
            role='listitem'
            data-qa={qa}
        >
            <Flex direction='column' spacing='24'>
                <Grid alignItems='center' gridTemplateColumns='22px auto 32px' gap='16px'>
                    {IconFlag}

                    <Flex direction='column' justifyContent='space-between' spacing='0'>
                        <Text
                            font='label'
                            fontSize='large'
                            color={disabled ? 'midnight60' : 'midnight100'}
                            data-qa={domHelpers.generateDataQa(qa, 'currency-code')}
                        >
                            {currencyCode}
                        </Text>

                        <Text
                            font='label'
                            fontSize='small'
                            color={disabled ? 'midnight60' : 'midnight70'}
                            data-qa={domHelpers.generateDataQa(qa, 'currency-name')}
                        >
                            {currencyName}
                        </Text>
                    </Flex>

                    {!disabled && hover && controls}
                </Grid>

                <Flex justifyContent='space-between'>
                    <Flex spacing='4' direction='column'>
                        <Text font='label' fontSize='small' color={disabled ? 'midnight60' : 'midnight70'}>
                            {messages.balance}
                        </Text>

                        <Text
                            font='label'
                            fontSize='large'
                            color={disabled ? 'midnight60' : 'midnight100'}
                            data-qa={domHelpers.generateDataQa(qa, 'balance')}
                        >
                            {balance}
                        </Text>
                    </Flex>

                    {canConvert && (
                        <Flex direction='column' justifyContent='end'>
                            <Button
                                color='blue10'
                                startIcon={<ConvertIcon />}
                                size='xsmall'
                                disabled={disabled}
                                data-qa={domHelpers.generateDataQa(qa, 'convert-button')}
                                onClick={handleConvertClick}
                                variant='outline'
                            >
                                {messages.convert}
                            </Button>
                        </Flex>
                    )}
                </Flex>
            </Flex>
        </Box>
    ));

    return hoverable;
}) as ComponentType<WalletCardProps> & ChildrenComponents;

WalletCard.displayName = 'WalletCard';
WalletCard.Loading = Loading;
